import { TZDate } from "@date-fns/tz";
import { format, parseISO } from "date-fns";

/** Everything in this system happens on Dubai time. */
export const DUBAI = "Asia/Dubai";

/** A plain calendar date, with no time and therefore no timezone. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Turn an API value into something `format` can render.
 *
 * A leave date or an expiry date is a calendar date, not an instant: 11 March
 * is 11 March wherever the reader happens to be. Converting one through a
 * timezone shifts it by a day for anyone far enough east or west, so date-only
 * values are built directly from their parts. Timestamps are real instants and
 * are shown in Dubai time, where the business operates.
 */
function toDisplayDate(value: string | Date): Date | null {
  if (typeof value !== "string") {
    return Number.isNaN(value.getTime()) ? null : new TZDate(value, DUBAI);
  }
  if (DATE_ONLY.test(value)) {
    const [year, month, day] = value.split("-").map(Number);
    return new Date(year!, month! - 1, day!);
  }
  const parsed = parseISO(value);
  return Number.isNaN(parsed.getTime()) ? null : new TZDate(parsed, DUBAI);
}

export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const date = toDisplayDate(value);
  return date ? format(date, "d MMM yyyy") : "—";
}

export function formatLongDate(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const date = toDisplayDate(value);
  return date ? format(date, "d MMMM yyyy") : "—";
}

export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const date = toDisplayDate(value);
  return date ? format(date, "d MMM yyyy, HH:mm") : "—";
}

/** A date range written the way a person would say it. */
export function formatDateRange(start: string, end: string): string {
  const from = toDisplayDate(start);
  const to = toDisplayDate(end);
  if (!from || !to) return "—";
  if (start === end) return format(from, "d MMMM yyyy");
  if (from.getFullYear() !== to.getFullYear()) {
    return `${format(from, "d MMM yyyy")} to ${format(to, "d MMM yyyy")}`;
  }
  if (from.getMonth() !== to.getMonth()) {
    return `${format(from, "d MMM")} to ${format(to, "d MMM yyyy")}`;
  }
  return `${format(from, "d")} to ${format(to, "d MMMM yyyy")}`;
}

/**
 * Move a calendar date by a number of days.
 *
 * Done on the date parts in UTC: `new Date("2027-03-10T00:00:00")` is parsed
 * as local midnight, and converting that back through `toISOString` loses a
 * day for any reader east of Greenwich.
 */
export function shiftDays(iso: string, delta: number): string {
  const [year, month, day] = iso.split("-").map(Number);
  const date = new Date(Date.UTC(year!, month! - 1, day!));
  date.setUTCDate(date.getUTCDate() + delta);
  return date.toISOString().slice(0, 10);
}

type Amount = string | number | null | undefined;

const unset = (v: Amount) => v === null || v === undefined || v === "";

/**
 * Gross pay, which the database computes and so never leaves null.
 *
 * An employee with no salary entered has a computed gross of 0, which would
 * read as "paid nothing" rather than "not filled in yet". Four components
 * make it up: basic, allowances, cash risk for whoever handles cash, and
 * "other", which HR asked for because some pay fits none of those. Cash risk
 * came last and is optional, so it is the last argument.
 */
export function formatGross(
  gross: Amount,
  basic: Amount,
  allowances: Amount,
  other?: Amount,
  cashRisk?: Amount,
): string {
  if (unset(basic) && unset(allowances) && unset(other) && unset(cashRisk)) return "—";
  return formatMoney(gross);
}

/**
 * The components in one line: "12,000.00 basic · 4,000.00 allowances ·
 * 300.00 cash risk · 500.00 other (transport)".
 *
 * Cash risk and other are only said when there is some, so a record without
 * them reads exactly as it did before there were such things. They are said
 * in the order the company numbers them: basic, allowances, cash risk, other.
 */
export function formatPayParts(
  basic: Amount,
  allowances: Amount,
  other?: Amount,
  otherNote?: string | null,
  cashRisk?: Amount,
): string {
  const parts = [`${formatMoney(basic)} basic`, `${formatMoney(allowances)} allowances`];
  if (!unset(cashRisk) && Number(cashRisk) > 0) {
    parts.push(`${formatMoney(cashRisk)} cash risk`);
  }
  if (!unset(other) && Number(other) > 0) {
    const note = otherNote?.trim();
    parts.push(`${formatMoney(other)} other${note ? ` (${note})` : ""}`);
  }
  return parts.join(" · ");
}

/** Money always carries two decimals; the currency label sits outside. */
export function formatMoney(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const amount = typeof value === "string" ? Number(value) : value;
  if (Number.isNaN(amount)) return "—";
  return amount.toLocaleString("en-AE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Leave balances read as 22.5, not 22.50 or 22. */
export function formatDays(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const days = typeof value === "string" ? Number(value) : value;
  if (Number.isNaN(days)) return "—";
  return Number.isInteger(days) ? String(days) : days.toFixed(1);
}

/** Overtime is stored in minutes and read as hours and minutes. */
export function formatMinutes(total: number | null | undefined): string {
  if (total === null || total === undefined) return "—";
  const sign = total < 0 ? "-" : "";
  const absolute = Math.abs(total);
  const hours = Math.floor(absolute / 60);
  const minutes = absolute % 60;
  return `${sign}${hours}:${String(minutes).padStart(2, "0")}`;
}

/** How long something lasts, said the way a person would: "1 hour 30 mins". */
export function formatSpan(totalMinutes: number | null | undefined): string {
  if (totalMinutes === null || totalMinutes === undefined || totalMinutes <= 0) {
    return "—";
  }
  const hours = Math.floor(totalMinutes / 60);
  const minutes = Math.round(totalMinutes % 60);
  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
  if (minutes > 0) parts.push(`${minutes} ${minutes === 1 ? "min" : "mins"}`);
  return parts.join(" ");
}

export function formatHours(totalMinutes: number | null | undefined): string {
  if (totalMinutes === null || totalMinutes === undefined) return "—";
  return (totalMinutes / 60).toFixed(1);
}

/** "on_leave" reads as "On leave" wherever a raw enum reaches the screen. */
export function humanise(value: string | null | undefined): string {
  if (!value) return "—";
  const spaced = value.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** The small words a family name carries with it. */
const PARTICLES = new Set(["al", "el", "bin", "bint", "ibn", "abu", "van", "von", "de", "da", "del"]);

/**
 * "Mohammed Abdul Rahman Al Hashimi" as "Mohammed Al Hashimi".
 *
 * Full names on a passport run to five or six words, and a list of who is
 * where has no room for them. The first word and the last are what people
 * call each other by. A family name that starts with a particle — Al, Bin,
 * El — keeps it, because "Hashimi" alone is not anybody's name.
 */
export function firstAndLastName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 2) return parts.join(" ");
  const first = parts[0]!;
  const last = parts[parts.length - 1]!;
  const before = parts[parts.length - 2]!;
  if (PARTICLES.has(before.toLowerCase())) {
    return `${first} ${before} ${last}`;
  }
  return `${first} ${last}`;
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}
